import { PlusIcon, LinkBreakIcon, PlugIcon, CheckCircleIcon, WarningCircleIcon } from '@phosphor-icons/react';
import { Button } from 'components/button/Button';
import { Card } from 'components/Card';
import { Formik } from 'components/form/formik';
import diff from 'object-diff';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import * as Yup from 'yup';
import { captureException } from '@sentry/react';

import { InputField } from '../../../components/input/InputField';
import { CheckboxField } from '../../../components/checkbox/CheckboxField';
import { FormDialog } from '../../../components/dialog/FormDialog';
import { Pill } from '../../../components/pill/Pill';
import { updateGeneralSettings } from '../models';
import { getDisplayError } from '../../../utils/get-display-error';
import { useAllGeneralSettings } from '../all-settings-context';
import { connectTeleroute, disconnectTeleroute } from '../../spot-pricing/models';

const settingsSchema = Yup.object().shape({
  telerouteEnabled: Yup.bool().required('Vereist'),
  telerouteOwnerLogin: Yup.string().required('Vereist'),
});

interface ITelerouteSettingsValues {
  telerouteEnabled: boolean;
  telerouteOwnerLogin: string;
}

const connectSchema = Yup.object().shape({
  username: Yup.string().required('Vereist'),
  password: Yup.string().required('Vereist'),
});

export const TelerouteSettingsPage = () => {
  const { settings, refreshData } = useAllGeneralSettings();
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const isConnected = Boolean(settings.telerouteIsConnected);

  const initialValues: ITelerouteSettingsValues = useMemo(() => {
    return {
      telerouteEnabled: settings.telerouteEnabled ?? false,
      telerouteOwnerLogin: settings.telerouteOwnerLogin ?? '',
    };
  }, [settings]);

  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      await disconnectTeleroute();
      toast.success('Teleroute account losgekoppeld');
      refreshData();
    } catch (err: any) {
      captureException(err);
      toast.error('Kon Teleroute niet loskoppelen: ' + getDisplayError(err));
    } finally {
      setIsDisconnecting(false);
    }
  };

  return (
    <div className="grid gap-4">
      <Card
        title="Accountkoppeling"
        subtitle="Nodig om orders als vrachtaanbod op de Teleroute spotmarkt te plaatsen."
        rightHeading={
          <Pill color={isConnected ? 'positive' : 'warning'}>{isConnected ? 'Gekoppeld' : 'Niet gekoppeld'}</Pill>
        }
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm">
            {isConnected ? (
              <>
                <CheckCircleIcon className="w-5 h-5 shrink-0 text-positive-text" weight="fill" />
                <span className="text-fg">Teleroute account is gekoppeld</span>
              </>
            ) : (
              <>
                <WarningCircleIcon className="w-5 h-5 shrink-0 text-warning-text" weight="fill" />
                <span className="text-fg">Teleroute account is niet gekoppeld</span>
              </>
            )}
          </div>

          {isConnected ? (
            <Button
              color="danger"
              onTrigger={handleDisconnect}
              isDisabled={isDisconnecting}
              isLoading={isDisconnecting}
              iconLeft={<LinkBreakIcon className="button-icon" />}
            >
              Loskoppelen
            </Button>
          ) : (
            <FormDialog
              triggerText={
                <span className="flex items-center gap-1">
                  <PlugIcon className="button-icon" />
                  Koppelen
                </span>
              }
              triggerColor="primary"
              title="Teleroute koppelen"
              submitText="Koppelen"
              initialValues={{ username: '', password: '' }}
              validationSchema={connectSchema}
              onSubmit={async (values, { resetForm }) => {
                const result = await connectTeleroute(values);
                if (!result.success) {
                  throw new Error(result.errorMessage || 'Koppelen mislukt');
                }
                toast.success('Teleroute account gekoppeld');
                resetForm();
                refreshData();
              }}
            >
              <InputField labelText="Teleroute gebruikersnaam" type="text" name="username" />
              <InputField labelText="Teleroute wachtwoord" type="password" name="password" />
            </FormDialog>
          )}
        </div>
      </Card>

      <Formik
        initialValues={initialValues}
        validationSchema={settingsSchema}
        enableReinitialize
        onSubmit={async (values) => {
          try {
            const patch: Partial<typeof values> = diff(initialValues, values);
            await updateGeneralSettings(patch);
            toast.success('Teleroute instellingen aangepast');
          } catch (err: any) {
            captureException(err);
            toast.error('Kon Teleroute instellingen niet aanpassen: ' + getDisplayError(err));
          }
        }}
      >
        {({ handleSubmit, isSubmitting }) => (
          <form onSubmit={handleSubmit} className="grid gap-4">
            <Card title="Instellingen">
              <CheckboxField
                labelText="Teleroute actief"
                name="telerouteEnabled"
                helperText="Zet het plaatsen van vrachtaanbod aan of uit."
              />
              <InputField
                labelText="Owner login"
                type="text"
                name="telerouteOwnerLogin"
                helperText="De Teleroute login die als eigenaar van het aanbod wordt getoond."
              />
            </Card>

            <div>
              <Button
                type="submit"
                color="primary"
                isDisabled={isSubmitting}
                isLoading={isSubmitting}
                iconLeft={<PlusIcon className="button-icon" />}
              >
                Pas instellingen aan
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};
